import React, { useEffect, useRef, useState } from "react";
import Spacing from "../Spacing";
import { Search, UserRoundPlus } from "lucide-react";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { can_visit, getPractice, notify, numberWithCommas } from "../../helpers";
import { addNewUserWithoutToken, deleteUser, fetchAllUserProviders, fetchAllUsers, fetchRoles, updateUser } from "../../services/users";
import AllUsersDataTable from "../DataTable/AllUsersDataTable";
import DataTablePagination from "../DataTablePagination";
import AddUserModal from "../Modal/AddUserModal";

export default function Users({selectedPracticeChange, fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }
  const [usersList, setUsersList] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const isFetched = useRef(false);
  const [updateLoader, setUpdateLoader] = useState(false);
  const [addUserModal, setAddUserModal] = useState(false);
  const [addUserLoader, setAddUserLoader] = useState(false);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [allProviders, setAllProviders] = useState([]);
  const getAllProviders = async () => {
    try {
      const res = await fetchAllUserProviders(account_id, location_id, 32, true);
      if (res?.data) {
        setAllProviders(res?.data);
      }
    } catch (err) { 
      // console.log("fetchAllUserProviders error", err);
    }

  };

  useEffect(() => {
    getAllProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    location_id: location_id,
    account_id: account_id,
    search_term: "",
    order_by: [],
  });

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  useEffect(() => {
    if (!isFetched.current) {
      loadAllRoles();
      isFetched.current = true;
    }
  }, []);

  const loadAllRoles = async () => {
    try {
      const response = await fetchRoles();
      if (response?.data) {
        const roleNames = response?.data?.filter((role) => role.status === true).map((role) => role.role_name);
        setUserRoles(roleNames);
      } else {
        setUserRoles([]);
      }
    } catch (error) {
      console.error("Error fetching loadAllRoles data:", error);
    }
  };

  const loadAllUsers = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const response = await fetchAllUsers( filter, account_id, location_id );
      const updatedData = response.data?.results?.map((row) => ({
        ...row,
        checked: false,
      }));
      setUsersList(updatedData);
      setPaginationCount({
        totalCount: response.data.count,
        totalPages: response.data.total_pages,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        notify("error", 'Something went wrong');
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const handleChangeSearchTerm = (event) => {
    setFilter({ ...filter, search_term: event.target.value });
    setDataLoading(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  const handleDeleteRecord = async (id) => {
    try {
      if(can_visit("settings_users_all_users_delete")){
        var response = await deleteUser(id, account_id, location_id);
        if (response?.detail) {
          notify("error", response?.detail);
        } else if(response?.data?.status === 'success'){
          await loadAllUsers(filter);
          notify("success", response?.data?.message);
        } else {
          notify("error",  response?.data?.message);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail);
      }
    }
  };

  const handleUpdatedUserInfo = async (data, handleClose = () => {}) => {
    const filteredData = data?.roles?.filter((input) =>userRoles?.includes(input));
    data.roles = filteredData

    if(can_visit("settings_users_all_users_edit")){
      setUpdateLoader(true);
      try {
        const response = await updateUser({ ...data });
        if(response){
          if(response?.detail) {
            notify("error", response?.detail);
          } else {
            if(response?.data?.status === "success"){
              notify("success", response?.data?.message, 5);
              await loadAllUsers(filter);
            } else {
              notify("error", response?.data?.message, 5);
            }
          }
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.errorMessage ||
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.response?.data ||
          "Oops! Server throws error";
        notify("error", errorMessage, 5);
      } finally {
        setUpdateLoader(false);
        handleClose();
      }
      // setDataLoading(true);
    } else {
      handleClose();
    }
  };

  const handleAddUserInfo = async (data) => {
    const filteredData = data?.roles?.filter((input) =>userRoles?.includes(input));
    data.roles = filteredData

    if(can_visit("settings_users_all_users_create")){
      setAddUserLoader(true);
      try {
        const response = await addNewUserWithoutToken({ ...data });
        if(response){
          if(response?.detail) {
            notify("error", response?.detail);
          } else {
            if(response?.data?.status === "success"){
              notify("success", response?.data?.message, 5);
              await loadAllUsers(filter);
            } else {
              notify("error", response?.data?.message, 5);
            }
          }
        }
      } catch (error) {
        if (error?.response && error?.response?.data && error?.response?.data?.detail) {
          notify("error", error?.response?.data?.detail, 0.5);
        }
      } finally {
        setAddUserLoader(false);
        handleDrawerClose();
      }
    } else {
      handleDrawerClose();
    }
  };

  const handleDrawerClose = () => {
    setAddUserModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  useEffect(() => {
    loadAllUsers(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  useDocumentTitle("All Users");

  return (
    <>
      <div className="docpro_page_heading_wrap mb-0">
        <h2 className="docpro_page_heading docpro_fs_24">
          All Users
          <span>{numberWithCommas(paginationCount.totalCount)}</span>
        </h2>
        <div className="docpro_page_heading_btns">
          {(can_visit("settings") && can_visit("settings_users") && can_visit("settings_users_all_users_create")) && (
            <button
              className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
              onClick={() => {
                setAddUserModal(true);
                setPreviousScrollPosition(window.scrollY);
                window.scrollTo(0, 0);
              }}
            >
              <UserRoundPlus width={20} height={20} />
              <span>Add User</span>
            </button>
          )} 
        </div>
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <form className="docpro_search_form">
            <input
              type="text"
              placeholder="Search"
              className="docpro_search_input"
              onChange={handleChangeSearchTerm}
            />
            <button className="docpro_search_btn">
              <Search width={20} height={20} />
            </button>
          </form>
        </div>

        <AllUsersDataTable
          allProviders={allProviders}
          tableData={usersList || []}
          handleDeleteRecord={handleDeleteRecord}
          reloadData={loadAllUsers}
          reloadDataFilter={filter}
          handleUpdatedUserInfo={handleUpdatedUserInfo}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          userRoles={userRoles}
          updateLoader={updateLoader}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
        {(usersList.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {addUserModal && (
        <AddUserModal
          allProviders={allProviders}
          openModal={addUserModal}
          setOpenModal={setAddUserModal}
          account_id={account_id}
          location_id={location_id}
          userRoles={userRoles}
          handleAddUserInfo={handleAddUserInfo}
          addUserLoader={addUserLoader}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
